'use client'

import * as React from 'react'
import { Send, CheckCircle2, AtSign, User, MessageSquare, Loader2, ShoppingBag, BarChart2 } from 'lucide-react'
import { careersEmail } from '@/lib/copy/careers'

const ECOM_PLATFORMS = [
  'Shopify', 'Shopify Plus', 'WooCommerce', 'BigCommerce',
  'Magento / Adobe Commerce', 'Headless / custom', 'Multiple platforms', 'Other', 'Not applicable',
]

const CURRENT_UGC_TOOLS = [
  'Idukki (already a customer!)',
  'Yotpo', 'Bazaarvoice', 'Okendo', 'Stamped', 'Loox',
  'Taggshop', 'Flowbox', 'Nosto', 'Manual / none', 'Other',
]

export function CareersInterestForm() {
  const [status, setStatus] = React.useState<'idle' | 'submitting' | 'success' | 'error'>('idle')
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null)

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (status === 'submitting') return
    setStatus('submitting')
    setErrorMessage(null)

    const data = new FormData(e.currentTarget)
    const payload = {
      name:         String(data.get('name')         || '').trim(),
      email:        String(data.get('email')        || '').trim(),
      ecomPlatform: String(data.get('ecomPlatform') || '').trim(),
      currentTool:  String(data.get('currentTool')  || '').trim(),
      message:      String(data.get('message')      || '').trim(),
    }

    try {
      const res = await fetch('/api/careers/interest', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      })
      const json = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string }
      if (!res.ok || !json.ok) {
        setStatus('error')
        setErrorMessage(json.error || `Server responded ${res.status}`)
        return
      }
      setStatus('success')
    } catch {
      setStatus('error')
      setErrorMessage('Network error — please try again or email us directly.')
    }
  }

  if (status === 'success') {
    return (
      <div className="mt-8 flex flex-col items-center gap-3 rounded-2xl border bg-card p-8 text-center">
        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-600">
          <CheckCircle2 className="h-5 w-5" />
        </span>
        <p className="font-display text-lg font-semibold">Got it — Rohin will be in touch.</p>
        <p className="text-sm text-muted-foreground">
          Usually within 72 hours. You can also reach us at{' '}
          <a href={`mailto:${careersEmail}`} className="font-medium text-primary underline-offset-4 hover:underline">{careersEmail}</a>.
        </p>
      </div>
    )
  }

  return (
    <form
      onSubmit={onSubmit}
      className="mt-8 rounded-2xl border bg-card p-6 md:p-8"
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            Name <span className="ml-0.5 text-primary">*</span>
          </span>
          <span className="relative mt-1.5 flex items-center">
            <User className="pointer-events-none absolute left-3 h-3.5 w-3.5 text-muted-foreground" />
            <input
              type="text"
              name="name"
              required
              placeholder="Rohin Aggarwal"
              className="block w-full rounded-lg border bg-background py-2.5 pl-9 pr-3 text-sm shadow-inner focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </span>
        </label>

        <label className="block">
          <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            Email <span className="ml-0.5 text-primary">*</span>
          </span>
          <span className="relative mt-1.5 flex items-center">
            <AtSign className="pointer-events-none absolute left-3 h-3.5 w-3.5 text-muted-foreground" />
            <input
              type="email"
              name="email"
              required
              placeholder="you@example.com"
              className="block w-full rounded-lg border bg-background py-2.5 pl-9 pr-3 text-sm shadow-inner focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </span>
        </label>

        <label className="block">
          <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            Primary e-com platform
          </span>
          <span className="relative mt-1.5 flex items-center">
            <ShoppingBag className="pointer-events-none absolute left-3 h-3.5 w-3.5 text-muted-foreground" />
            <select
              name="ecomPlatform"
              defaultValue=""
              className="block w-full rounded-lg border bg-background py-2.5 pl-9 pr-8 text-sm shadow-inner focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 appearance-none"
            >
              <option value="" disabled>Select one…</option>
              {ECOM_PLATFORMS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </span>
        </label>

        <label className="block">
          <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            Current UGC / social proof tool
          </span>
          <span className="relative mt-1.5 flex items-center">
            <BarChart2 className="pointer-events-none absolute left-3 h-3.5 w-3.5 text-muted-foreground" />
            <select
              name="currentTool"
              defaultValue=""
              className="block w-full rounded-lg border bg-background py-2.5 pl-9 pr-8 text-sm shadow-inner focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 appearance-none"
            >
              <option value="" disabled>Select one…</option>
              {CURRENT_UGC_TOOLS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </span>
        </label>
      </div>

      <label className="mt-4 block">
        <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
          What would you work on at Idukki?
        </span>
        <span className="relative mt-1.5 flex">
          <MessageSquare className="pointer-events-none absolute left-3 top-3 h-3.5 w-3.5 text-muted-foreground" />
          <textarea
            name="message"
            rows={4}
            placeholder="200 words or fewer. What problem do you want to solve? What would you build?"
            className="block w-full rounded-lg border bg-background py-2.5 pl-9 pr-3 text-sm shadow-inner focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
        </span>
      </label>

      {status === 'error' && (
        <p className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[13px] text-red-700">
          {errorMessage || 'Something went wrong. Please try again, or email us directly.'}
        </p>
      )}

      <div className="mt-5 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-[11px] text-muted-foreground">
          No role attached — just a conversation. We delete expressions of interest after 12 months.
        </p>
        <button
          type="submit"
          disabled={status === 'submitting'}
          className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-sm transition hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {status === 'submitting' ? (
            <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Sending…</>
          ) : (
            <>Send note <Send className="h-3.5 w-3.5" /></>
          )}
        </button>
      </div>
    </form>
  )
}
